import React, { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import BANNERS_API_V1 from 'api/banners/v1'
import styles from './slider-banner.module.scss'
import { Grid, Typography } from 'gu-ui-kit'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import NavArrows from 'components/Banners/SliderBanner/subcomponents/NavArrows'
import Buttons from 'components/Banners/SliderBanner/subcomponents/Buttons'
import clsx from 'clsx'

const SliderBanner = ({ className, placement, network = null }) => {
  const { i18n } = useSelector((state) => state.app)
  const [banners, setBanners] = useState([])
  const [currentBannerNum, setCurrentBannerNum] = useState(0)
  const [loading, setLoading] = useState(true)
  const breakpoints = useBreakpoint()

  useEffect(() => {
    if (!network) {
      return
    }

    setBanners([])
    setLoading(true)

    BANNERS_API_V1.fetchBanners({
      params: { placement, network, locale: i18n },
    })
      .then(({ response }) => {
        setBanners(response?.data || [])
        setCurrentBannerNum(0)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [i18n, network])

  const currentBanner = useMemo(() => banners[currentBannerNum] ?? {}, [banners, currentBannerNum])

  const fontSizes = useMemo(() => {
    switch (true) {
      case breakpoints.sm:
        return { title: 'Subtitle-20', text: 'Body-15' }
      case breakpoints.md:
        return { title: 'Subtitle-30', text: 'Body-15' }
      default:
        return { title: 'Subtitle-40', text: 'Body-20' }
    }
  }, [breakpoints])

  if (!loading && banners.length === 0) {
    return null
  }

  return loading ? null : (
    <div
      className={clsx(styles['container'], className, {
        [styles['container_loading']]: loading,
      })}
      style={currentBanner && { backgroundImage: `url(${currentBanner.image})` }}
    >
      <Grid
        className={styles['content']}
        direction={'column'}
        gap={24}
        justifyContent={'space-between'}
      >
        <Grid direction={'column'} justifyContent={'space-between'}>
          {!currentBanner ? null : (
            <>
              <Grid direction={'column'} gap={breakpoints.sm ? 8 : 12}>
                <Typography
                  variant={fontSizes.title}
                  color={'Label-Fifth'}
                  className={styles['title']}
                >
                  {currentBanner.title}
                </Typography>
                <Typography variant={fontSizes.text} component={'div'}>
                  {currentBanner.text}
                </Typography>
              </Grid>

              {!breakpoints.sm && (
                <Grid
                  className={'margin-top-16'}
                  wrap={false}
                  direction={'row'}
                  justifyContent={'space-between'}
                  alignItems={'center'}
                >
                  <Grid gap={16} direction={'row'} wrap={false}>
                    <Buttons currentBanner={currentBanner} />
                  </Grid>
                </Grid>
              )}
            </>
          )}
        </Grid>
      </Grid>
      {!breakpoints.sm ? (
        <NavArrows
          banners={banners}
          currentBannerNum={currentBannerNum}
          setCurrentBannerNum={setCurrentBannerNum}
        />
      ) : (
        <Grid
          className={'margin-top-16'}
          wrap={false}
          direction={'row'}
          justifyContent={'space-between'}
          alignItems={'center'}
        >
          <Grid gap={16} direction={'row'} wrap={false}>
            <Buttons currentBanner={currentBanner} />
          </Grid>
          <div>
            <NavArrows
              banners={banners}
              currentBannerNum={currentBannerNum}
              setCurrentBannerNum={setCurrentBannerNum}
            />
          </div>
        </Grid>
      )}
    </div>
  )
}

export default SliderBanner

SliderBanner.propTypes = {
  placement: PropTypes.string,
  network: PropTypes.string,
  className: PropTypes.string,
}
